import {sweetAPI} from 'app/store/sweet-api';
import {showLoadingBar, showMessages} from 'app/store/sweet-api/utils';
import {ACCEPTANCE_API_URL, ACCEPTANCE_STATUS_API_URL} from 'app/urls';
import {gt} from 'app/utils/i18n';
import {ACCEPTANCE_RTK_TAGS} from 'app/utils/rtk-tags';

import type {
  AcceptanceSerializedType,
  BaseDocumentContentType,
  ListResponseType,
} from 'sweet-types';

export type SetAcceptanceStatusProps = {
  id: number;
  document_id: number;
  content_type: BaseDocumentContentType;
  status?: boolean;
};

export type AcceptanceFetchType = {
  object_pk: number;
  content_type: BaseDocumentContentType;
};

export const extendedAPI = sweetAPI
  .enhanceEndpoints({
    addTagTypes: [
      ACCEPTANCE_RTK_TAGS.TAGS,
      ACCEPTANCE_RTK_TAGS.VERSION_STATUS_TAG,
    ],
  })
  .injectEndpoints({
    endpoints: (build) => ({
      setAcceptanceStatus: build.mutation<unknown, SetAcceptanceStatusProps>({
        query: ({id, ...body}) => ({
          url: `${ACCEPTANCE_STATUS_API_URL}${id}/`,
          method: 'PATCH',
          body,
        }),
        invalidatesTags: () => [
          {type: ACCEPTANCE_RTK_TAGS.VERSION_STATUS_TAG, id: 'list'},
        ],
        onQueryStarted: (args, api) => {
          showLoadingBar(args, api);
          showMessages({
            onError: () =>
              gt('Unable to set acceptance status on the version.'),
          })(args, api);
        },
      }),
      acceptance: build.query<
        ListResponseType<AcceptanceSerializedType>,
        AcceptanceFetchType
      >({
        query: (params) => ({
          url: ACCEPTANCE_API_URL,
          params,
        }),
        providesTags: [{type: ACCEPTANCE_RTK_TAGS.TAGS, id: 'list'}],
        onQueryStarted: (args, api) => {
          showLoadingBar(args, api);
          showMessages({
            onError: () =>
              gt('Could not load %(content_type)s approvals', {
                content_type: args.content_type,
              }),
          })(args, api);
        },
      }),
      requestVersionAcceptance: build.mutation<
        unknown,
        {
          versionId: number | string;
          versionContentType: AcceptanceSerializedType['content_type'];
          message: string;
          user_ids: Array<string>;
        }
      >({
        query: ({versionId, versionContentType, message, user_ids}) => ({
          method: 'POST',
          url: ACCEPTANCE_API_URL,
          body: {
            object_id: versionId,
            content_type: versionContentType,
            message,
            user_ids,
          },
        }),
        invalidatesTags: () => [{type: ACCEPTANCE_RTK_TAGS.TAGS, id: 'list'}],
        onQueryStarted: showMessages({
          onError: () => gt('Could not request approval.'),
          onSuccess: () =>
            gt('An approval request was sent to the selected managers.'),
        }),
      }),
    }),
  });

// https://redux-toolkit.js.org/rtk-query/usage/queries#hook-types
export const {
  useAcceptanceQuery: useAcceptance,
  useRequestVersionAcceptanceMutation: useRequestVersionAcceptance,
  useSetAcceptanceStatusMutation: useSetAcceptanceStatus,
} = extendedAPI;
